import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { bikeAPI, reviewAPI, promoAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { Badge, Spinner, EmptyState } from '../../components/common/Misc';
import Button from '../../components/common/Button';
import Input  from '../../components/common/Input';
import styles from './BikeDetailPage.module.css';
import { calcPricing } from '../../utils/pricing';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
const resolveImg = (url) => (url && url.startsWith('http') ? url : `${API_BASE}${url}`);


const BikeDetailPage = () => {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const { user }    = useAuth();
  const { addToCart } = useCart();

  const [bike,     setBike]     = useState(null);
  const [reviews,  setReviews]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  // Booking form state
  const [startTime,  setStartTime]  = useState('');
  const [endTime,    setEndTime]    = useState('');
  const [promoCode,  setPromoCode]  = useState('');
  const [promoData,  setPromoData]  = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [available, setAvailable]  = useState(null);

  useEffect(() => {
    Promise.all([
      bikeAPI.getOne(id),
      reviewAPI.getForBike(id, { limit: 5 }),
    ]).then(([bikeRes, revRes]) => {
      setBike(bikeRes.data.data);
      setReviews(revRes.data.data);
    }).catch(() => navigate('/bikes'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Compute totals (daily rate only, GST included)
  const totals = (() => {
    if (!bike || !startTime || !endTime) return null;
    if (new Date(endTime) <= new Date(startTime)) return null;
    const discount = promoData?.discount_amount || 0;
    return calcPricing(bike, startTime, endTime, discount);
  })();

  const checkAvailability = async () => {
    if (!startTime || !endTime) return;
    try {
      const res = await bikeAPI.checkAvailability({ bike_id: id, start: startTime, end: endTime });
      setAvailable(res.data.available);
      if (!res.data.available) toast.error('Bike is not available for this time slot');
    } catch {
      toast.error('Could not check availability');
    }
  };

  const applyPromo = async () => {
    if (!promoCode || !totals) return;
    setPromoLoading(true);
    try {
      const res = await promoAPI.validate({ code: promoCode, booking_amount: totals.base });
      setPromoData(res.data.data);
      toast.success(`Promo applied! You save ₹${res.data.data.discount_amount}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid promo code');
      setPromoData(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!user)      { toast.error('Please login first'); return navigate('/login'); }
    if (!totals)    { toast.error('Select valid dates first'); return; }
    if (!available) { toast.error('Check availability first'); return; }

    addToCart({
      bike,
      startTime,
      endTime,
      days:           totals.days,
      totalAmount:    totals.base,
      promoCode:      promoData ? promoCode : null,
      discountAmount: totals.discountAmount,
      afterDiscount:  totals.afterDiscount,
      gst:            totals.gst,
      finalAmount:    totals.total,
    });
    toast.success('Added to cart!');
    navigate('/cart');
  };

  if (loading) return <Spinner />;
  if (!bike)   return null;

  return (
    <div className="container">
      <div className={styles.layout}>
        {/* Left: bike info */}
        <div className={styles.left}>
          <div className={styles.imgWrap}>
            {bike.images?.[0]
              ? <img src={resolveImg(bike.images[0])} alt={bike.name} className={styles.img} />
              : (
          <div className={styles.placeholder}>
            <svg viewBox="0 0 120 60" fill="none" style={{width:'60%',color:'rgba(196,181,253,.7)'}}>
              <circle cx="22" cy="44" r="14" stroke="currentColor" strokeWidth="4" fill="none"/>
              <circle cx="22" cy="44" r="5" fill="currentColor" opacity=".4"/>
              <circle cx="98" cy="44" r="14" stroke="currentColor" strokeWidth="4" fill="none"/>
              <circle cx="98" cy="44" r="5" fill="currentColor" opacity=".4"/>
              <path d="M36 44 L50 20 L72 20 L84 32 L98 30" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M50 20 L42 44" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
              <path d="M72 20 L72 12 L88 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )
            }
          </div>

          <div className={styles.info}>
            <div className={styles.titleRow}>
              <h1 className={styles.name}>{bike.name}</h1>
              <Badge label={bike.status} status={bike.status} />
            </div>
            <p className={styles.brand}>{bike.brand} · <span className={styles.type}>{bike.type}</span></p>
            {bike.avg_rating > 0 && (
              <div className={styles.rating}>
                {'★'.repeat(Math.round(bike.avg_rating))}{'☆'.repeat(5 - Math.round(bike.avg_rating))}
                <span className={styles.ratingText}>{bike.avg_rating} ({bike.review_count} reviews)</span>
              </div>
            )}
            <p className={styles.description}>{bike.description}</p>
            <div className={styles.priceRow}>
              <div className={styles.priceBox}>
                <span className={styles.priceVal}>₹{bike.price_per_day}</span>
                <span className={styles.priceLabel}>per day + 18% GST</span>
              </div>
            </div>
            <p className={styles.location}>📍 {bike.location}</p>
          </div>

          {/* Reviews */}
          <div className={styles.reviews}>
            <h2 className={styles.reviewTitle}>Reviews</h2>
            {reviews.length === 0
              ? <EmptyState icon="💬" title="No reviews yet" />
              : reviews.map(r => (
                <div key={r._id} className={styles.reviewCard}>
                  <div className={styles.reviewTop}>
                    <span className={styles.reviewer}>{r.user_id?.name || 'User'}</span>
                    <span className={styles.stars}>{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                  </div>
                  {r.comment && <p className={styles.comment}>{r.comment}</p>}
                </div>
              ))
            }
          </div>
        </div>

        {/* Right: booking form */}
        <div className={styles.right}>
          <div className={styles.bookingCard}>
            <h2 className={styles.bookingTitle}>Book this bike</h2>

            <div className={styles.dateFields}>
              <Input
                label="Start date & time"
                type="datetime-local"
                value={startTime}
                onChange={e => { setStartTime(e.target.value); setAvailable(null); setPromoData(null); }}
                min={new Date().toISOString().slice(0, 16)}
              />
              <Input
                label="End date & time"
                type="datetime-local"
                value={endTime}
                onChange={e => { setEndTime(e.target.value); setAvailable(null); setPromoData(null); }}
                min={startTime || new Date().toISOString().slice(0, 16)}
              />
            </div>

            <Button variant="ghost" fullWidth onClick={checkAvailability} disabled={!startTime || !endTime}>
              Check availability
            </Button>

            {available === true  && <p className={styles.availOk}>✅ Available for selected dates!</p>}
            {available === false && <p className={styles.availNo}>❌ Not available for selected dates.</p>}

            {/* Promo */}
            {totals && (
              <div className={styles.promoRow}>
                <input
                  className={styles.promoInput}
                  placeholder="Promo code"
                  value={promoCode}
                  onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoData(null); }}
                />
                <Button variant="secondary" size="sm" loading={promoLoading} onClick={applyPromo}>
                  Apply
                </Button>
              </div>
            )}

            {/* Summary */}
            {totals && (
              <div className={styles.summary}>
                <div className={styles.summaryRow}>
                  <span>Duration</span>
                  <span>{totals.days} day{totals.days > 1 ? 's' : ''}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>Base amount</span>
                  <span>₹{totals.base.toFixed(2)}</span>
                </div>
                {totals.discountAmount > 0 && (
                  <div className={`${styles.summaryRow} ${styles.discount}`}>
                    <span>Discount</span>
                    <span>−₹{totals.discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className={styles.summaryRow}>
                  <span>GST (18%)</span>
                  <span>₹{totals.gst.toFixed(2)}</span>
                </div>
                <div className={`${styles.summaryRow} ${styles.total}`}>
                  <span>Total payable</span>
                  <span>₹{totals.total.toFixed(2)}</span>
                </div>
              </div>
            )}

            <Button
              fullWidth size="lg"
              loading={bookingLoading}
              disabled={!available || !totals}
              onClick={handleAddToCart}
            >
              Add to cart
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BikeDetailPage;
